
import { toast } from "@/hooks/use-toast";
import { logError } from "@/lib/logger";
import { supabase } from "@/integrations/supabase/client";
import { queryClient } from "@/hooks/subscriptions/queryClient";
import { AuthUser } from "./types";

export const useAuthFunctions = (
  setUser: React.Dispatch<React.SetStateAction<AuthUser | null>>,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const fetchUserRoles = async (userId: string) => {
    const { data, error } = await supabase.rpc('get_user_roles', { user_id: userId });
    
    if (error) {
      console.error("Error fetching user roles:", error);
      return;
    }
    
    setUser(prev => {
      if (!prev) return null;
      return { ...prev, roles: data };
    });
  };

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        toast.error({
          title: "Login failed",
          description: error.message
        });
        throw error;
      }
      
      toast.success({
        title: "Welcome back!",
        description: "You've successfully logged in."
      });
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, password: string, name?: string, companyName?: string, skipEmailConfirmation = false, country?: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            companyName,
            company_name: companyName,
            country: country || 'US',
            eula_accepted_version: '2026-02-25',
            eula_accepted_at: new Date().toISOString(),
          },
          emailRedirectTo: `${window.location.origin}/login`,
        },
      });

      if (error) {
        toast.error(error.message);
        throw error;
      }
      
      if (skipEmailConfirmation && data.user) {
        toast.success("Account created successfully!");
      } else {
        toast.success("Account created! Please check your email for verification.");
      }
      
      return data.user;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error(error.message);
    } else {
      setUser(null);
      toast.success("Signed out successfully");
      window.location.href = '/';
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    try {
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error("Error getting session:", error);
        return null;
      }
      
      return data.session?.access_token || null;
    } catch (error) {
      console.error("Unexpected error getting token:", error);
      return null;
    }
  };

  const forgotPassword = async (email: string): Promise<void> => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        toast.error(error.message);
        throw error;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (token: string, newPassword: string): Promise<void> => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        toast.error(error.message);
        throw error;
      }
      
      toast.success("Password updated successfully");
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (data: { name?: string; companyName?: string }): Promise<void> => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          name: data.name,
          companyName: data.companyName,
        }
      });

      if (error) {
        toast.error(error.message);
        throw error;
      }

      // If company name changed, update the user's owned workspace name to match
      if (data.companyName) {
        const { data: session } = await supabase.auth.getSession();
        if (session?.session?.user?.id) {
          await supabase
            .from("workspaces")
            .update({ name: data.companyName })
            .eq("owner_id", session.session.user.id);
          
          // Refresh workspace data in the UI
          queryClient.invalidateQueries({ queryKey: ["workspaces"] });
        }
      }

      setUser(prev => {
        if (!prev) return null;
        return {
          ...prev,
          name: data.name || prev.name,
          companyName: data.companyName || prev.companyName,
        };
      });
      
      toast.success("Profile updated successfully");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    fetchUserRoles,
    login,
    register,
    logout,
    getAccessToken,
    forgotPassword,
    resetPassword,
    updateProfile
  };
};
